/**
 * Image compression for camera-captured uploads.
 *
 * Phones take 5-12 MB photos; we want ~500 KB before PUT-to-GCS so the
 * upload is fast on cellular and the GCS storage bill stays bounded. PDFs
 * pass through unchanged.
 *
 * Uses browser-image-compression (Pica under the hood) — already declared
 * in apps/carnaf-pwa/package.json.
 */

import imageCompression from 'browser-image-compression';

const TARGET_MAX_SIZE_MB = 0.6;
const TARGET_MAX_WIDTH = 1920;

export async function maybeCompressImage(file: File): Promise<File> {
  // PDFs go through untouched — they're already small and OCR needs them lossless.
  if (file.type === 'application/pdf') return file;

  // Files already under target — skip the round-trip.
  if (file.size <= TARGET_MAX_SIZE_MB * 1024 * 1024) return file;

  try {
    const compressed = await imageCompression(file, {
      maxSizeMB: TARGET_MAX_SIZE_MB,
      maxWidthOrHeight: TARGET_MAX_WIDTH,
      useWebWorker: true,
      fileType: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
    });
    // browser-image-compression returns a Blob, sometimes typed as File.
    return new File([compressed], file.name, {
      type: compressed.type || file.type,
      lastModified: file.lastModified,
    });
  } catch {
    // Compression failure should not block the upload. Fall back to the
    // original file; the server's 10MB cap still protects us.
    return file;
  }
}
